import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useRBAC } from '../hooks/useRBAC';
import type { UserRole, RolePermissions } from '../types/rbac';
import { ROLE_LABELS, PERMISSION_LABELS } from '../types/rbac';
import { HiShieldExclamation, HiHome, HiArrowNarrowRight } from 'react-icons/hi';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, only admin users (or ADMIN_EMAIL) can access */
  adminOnly?: boolean;
  /** Require specific role or one of multiple roles */
  requiredRole?: UserRole | UserRole[];
  /** Require specific granular permission */
  requiredPermission?: keyof RolePermissions;
}

export default function ProtectedRoute({
  children,
  adminOnly = false,
  requiredRole,
  requiredPermission,
}: ProtectedRouteProps) {
  const { user, loading, isAdmin, hasRole, hasPermission, role } = useRBAC();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          <span className="text-gray-400 text-sm">در حال اعتبارسنجی دسترسی...</span>
        </div>
      </div>
    );
  }

  // User must be authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Super admin satisfies all checks
  if (isAdmin) {
    return <>{children}</>;
  }

  let isAuthorized = true;
  let reason = '';

  if (adminOnly && !isAdmin) {
    isAuthorized = false;
    reason = 'این صفحه نیازمند سطح دسترسی مدیر کل (Super Admin) است.';
  } else if (requiredRole && !hasRole(requiredRole)) {
    isAuthorized = false;
    const rolesArr = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const roleNames = rolesArr.map((r) => ROLE_LABELS[r]?.title || r).join(' یا ');
    reason = `این بخش فقط برای کاربران دارای نقش ${roleNames} در دسترس است.`;
  } else if (requiredPermission && !hasPermission(requiredPermission)) {
    isAuthorized = false;
    const permLabel = PERMISSION_LABELS[requiredPermission] || requiredPermission;
    reason = `حساب شما دسترسی لازم جهت «${permLabel}» را ندارد.`;
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-16" dir="rtl">
        <div className="max-w-md w-full bg-zinc-900/90 border border-red-500/30 rounded-3xl p-8 text-center shadow-2xl backdrop-blur-xl">
          <div className="w-16 h-16 bg-red-500/10 text-red-400 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HiShieldExclamation className="w-10 h-10" />
          </div>

          <h2 className="text-2xl font-black text-white mb-2">عدم دسترسی به این بخش</h2>
          <p className="text-gray-400 text-sm mb-4 leading-relaxed">{reason}</p>

          <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-xs text-gray-400 mb-6 text-right">
            <div className="flex justify-between mb-1.5">
              <span>ایمیل شما:</span>
              <span className="font-mono text-white">{user.email}</span>
            </div>
            <div className="flex justify-between">
              <span>نقش فعلی شما:</span>
              <span className="font-bold text-amber-400">{ROLE_LABELS[role]?.title || role}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="flex items-center justify-center gap-2 bg-primary text-black font-bold px-5 py-3 rounded-xl hover:bg-primary/90 transition-all text-sm"
            >
              <HiHome className="w-4 h-4" />
              صفحه اصلی
            </Link>
            <button
              onClick={() => window.history.back()}
              className="flex items-center justify-center gap-2 bg-white/10 text-white font-medium px-5 py-3 rounded-xl hover:bg-white/20 transition-all text-sm"
            >
              <HiArrowNarrowRight className="w-4 h-4" />
              بازگشت
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
